
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Layout from '@/components/layout/Layout';
import { useCartStore } from '@/store/useCartStore';
import { toast } from '@/hooks/use-toast';
import CheckoutHero from '@/components/checkout/CheckoutHero';
import OrderSummary from '@/components/checkout/OrderSummary';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileCheckoutTabs from '@/components/checkout/MobileCheckoutTabs';
import DesktopCheckoutLayout from '@/components/checkout/DesktopCheckoutLayout';
import EmptyCartMessage from '@/components/checkout/EmptyCartMessage';
import { shippingOptions, paymentMethods } from '@/components/checkout/checkoutOptions';
import { useFlutterwavePayment } from '@/hooks/useFlutterwavePayment';
import PaymentScript from '@/components/checkout/PaymentScript';
import { CustomerFormValues } from '@/components/checkout/CustomerInformation';
import { ShippingFormValues } from '@/components/checkout/ShippingInformation';

const Checkout = () => {
  // Cart state
  const {
    items,
    totalPrice,
    clearCart
  } = useCartStore();

  // Navigation
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Payment integration
  const { makePayment } = useFlutterwavePayment();

  // Checkout state
  const [shipping, setShipping] = useState(shippingOptions[0].id);
  const [paymentMethod, setPaymentMethod] = useState(paymentMethods[0].id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("customer");
  const [orderReference] = useState(`ahavex-${uuidv4()}`);

  // Form state - Initialize with empty strings to satisfy the required fields
  const [customerInfo, setCustomerInfo] = useState<CustomerFormValues>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  const [shippingInfo, setShippingInfo] = useState<ShippingFormValues>({
    address: '',
    city: '',
    district: ''
  });

  // Handle form submissions
  const handleCustomerInfoSubmit = (data: CustomerFormValues) => {
    setCustomerInfo(data);
  };

  const handleShippingInfoSubmit = (data: ShippingFormValues) => {
    setShippingInfo(data);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get selected shipping option
  const selectedShipping = shippingOptions.find(option => option.id === shipping);

  // Calculated values
  const subtotal = totalPrice();
  const shippingCost = selectedShipping?.price || 0;
  const grandTotal = subtotal + shippingCost;
  const currency = items.length > 0 ? items[0]?.currency || 'RWF' : 'RWF';

  // Check if form is complete
  const isFormComplete = () => {
    if (
      !customerInfo.firstName || 
      !customerInfo.lastName || 
      !customerInfo.email || 
      !customerInfo.phone || 
      !shippingInfo.address || 
      !shippingInfo.city || 
      !shippingInfo.district
    ) {
      toast({
        title: "Incomplete Information",
        description: "Please fill in all required fields before placing your order.",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  // Handle order submission
  const handleSubmit = () => {
    if (!isFormComplete()) return;

    setIsSubmitting(true);
    window.scrollTo(0, 0);

    if (paymentMethod === 'flutterwave') {
      makePayment({
        customerInfo,
        amount: grandTotal,
        currency,
        ref: orderReference
      });
      setIsSubmitting(false);
    } else {
      // Simulate API call for other payment methods
      setTimeout(() => {
        setIsSubmitting(false);
        clearCart();
        toast({
          title: "Order successful!",
          description: "Your order has been placed successfully."
        });
        navigate('/');
      }, 1500);
    }
  };

  return <Layout>
      <PaymentScript />
      <div className="max-w-7xl mx-auto px-4 py-0 mt-16">
        {items.length === 0 ? <EmptyCartMessage /> : <>
            <CheckoutHero />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Mobile and Desktop Checkout Layout */}
              <div className="lg:col-span-2">
                {isMobile ? (
                  <MobileCheckoutTabs 
                    activeTab={activeTab} 
                    setActiveTab={setActiveTab} 
                    shippingOptions={shippingOptions} 
                    selectedShipping={shipping} 
                    setShipping={setShipping} 
                    paymentMethods={paymentMethods} 
                    selectedPayment={paymentMethod} 
                    setPaymentMethod={setPaymentMethod} 
                    items={items} 
                    subtotal={subtotal} 
                    shippingCost={shippingCost} 
                    currency={currency} 
                    onSubmit={handleSubmit} 
                    isSubmitting={isSubmitting}
                    handleCustomerInfoSubmit={handleCustomerInfoSubmit}
                    handleShippingInfoSubmit={handleShippingInfoSubmit}
                  />
                ) : (
                  <DesktopCheckoutLayout 
                    shippingOptions={shippingOptions} 
                    selectedShipping={shipping} 
                    setShipping={setShipping} 
                    paymentMethods={paymentMethods} 
                    selectedPayment={paymentMethod} 
                    setPaymentMethod={setPaymentMethod} 
                    currency={currency}
                    handleCustomerInfoSubmit={handleCustomerInfoSubmit}
                    handleShippingInfoSubmit={handleShippingInfoSubmit}
                  />
                )}
              </div>
              
              {/* Order Summary - Hidden on mobile until payment tab, always visible on desktop */}
              {(!isMobile || activeTab === "payment") && (
                <div className="lg:col-span-1">
                  <OrderSummary 
                    items={items} 
                    subtotal={subtotal} 
                    shippingCost={shippingCost} 
                    currency={currency} 
                    onSubmit={handleSubmit} 
                    isSubmitting={isSubmitting} 
                  />
                </div>
              )}
            </div>
          </>}
      </div>
    </Layout>;
};

export default Checkout;
